import { useQuery } from "@tanstack/react-query"
import { useState, useRef, useLayoutEffect } from "react"
import { Mail as MailIcon, MapPin, Phone, User, Folder, ExternalLink } from "lucide-react"

type Contact = {
  id: number
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  phone_mobile?: string
  phone_home?: string
  street?: string
  zipcode?: string
  city?: string
  address?: string
}
type Project = {
  id: number
  name: string
  title?: string
  code?: string
  status?: string
  status_code?: number | null
  step_id?: number | null
  type_id?: number | null
}
type Step = {
  id: number; name: string; status_code?: number | null; type_id: number
}

/**
 * Zeigt beim Hover ein Popover mit vollen HERO-Kontakt/Projekt-Infos (name, tel, email,
 * adresse, alle projekte mit status). Lazy-Fetch via /api/v1/hero/lookup.
 * Genutzt ueberall wo hero_contact_id oder hero_project_id verknuepft sind —
 * Email-Liste, Sipgate, Meiti, Dashboard, Tasks.
 */
export function HeroInfoPopover({
  tenantSlug, contactId, projectId, children,
}: {
  tenantSlug: string
  contactId?: number | string | null
  projectId?: number | string | null
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | null>(null)

  const q = useQuery<{ contact: Contact | null; projects: Project[]; steps: Step[] }>({
    queryKey: ["hero-lookup", tenantSlug, contactId || null, projectId || null],
    queryFn: async () => {
      const p = new URLSearchParams({ tenant: tenantSlug })
      if (contactId) p.set("contact_id", String(contactId))
      if (projectId) p.set("project_id", String(projectId))
      const res = await fetch(`/api/v1/hero/lookup?${p}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open && !!tenantSlug && !!(contactId || projectId),
    // Kurz halten: der Status eines frisch angelegten Vorgangs wechselt
    // Sekunden spaeter (Anlegen → Termin gebucht → „Vor-Ort Termin"). Mit
    // einer Minute Haltbarkeit zeigte das Popover danach noch minutenlang
    // „Anfrage eingegangen" (gemeldet 12.08.2026). Geladen wird ohnehin erst
    // beim Aufklappen.
    staleTime: 1000 * 15,
  })

  const onEnter = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    setOpen(true)
  }
  const onLeave = () => {
    // Kleiner Delay damit Maus ins Popover selbst rueberziehen kann
    closeTimer.current = window.setTimeout(() => setOpen(false), 150)
  }

  const contact = q.data?.contact
  const projects = q.data?.projects || []
  const steps = q.data?.steps || []

  // 🔑 Zuerst ueber die Schritt-Nummer. Der status_code ist NICHT eindeutig:
  // bei Sanitec tragen „Besichtigungstermin vereinbaren" und „…durchfuehren"
  // beide die 400 — wer darueber sucht, zeigt den erstbesten und damit
  // regelmaessig den falschen Schritt an. Bleibt als Notnagel, falls HERO
  // keine step_id mitliefert.
  const getPipelineStatus = (p: Project) => {
    if (p.step_id) {
      const exakt = steps.find((s) => String(s.id) === String(p.step_id))
      if (exakt) return exakt.name
    }
    if (!p.status_code || !p.type_id) return p.status
    const match = steps.find((s) => String(s.type_id) === String(p.type_id) && String(s.status_code) === String(p.status_code))
    return match?.name || p.status
  }

  // Viewport-aware positioning: flip left/right wenn Popover sonst aus dem Bild ragen wuerde.
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const [align, setAlign] = useState<"left" | "right">("left")
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    const estimatedWidth = 420 // maxWidth
    const padding = 12
    const spaceRight = window.innerWidth - rect.left - padding
    if (spaceRight < estimatedWidth) setAlign("right")
    else setAlign("left")
  }, [open])

  return (
    <span
      ref={anchorRef}
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {children}
      {open && (
        <div
          ref={popoverRef}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          style={{
            position: "absolute", top: "calc(100% + 6px)",
            [align]: 0 as any,
            zIndex: 9999,
            background: "var(--bg-card)", border: "1px solid var(--border-color)",
            borderRadius: 10, boxShadow: "0 10px 32px rgba(0,0,0,0.12)",
            padding: 12, minWidth: 320, maxWidth: 420,
            fontSize: 12, color: "var(--text)",
            textAlign: "left",
          }}
        >
          {q.isLoading && <div style={{ color: "var(--text-muted)" }}>Lädt…</div>}
          {q.isError && <div style={{ color: "var(--danger-color)" }}>Fehler beim Laden</div>}
          {!q.isLoading && !q.isError && !contact && projects.length === 0 && (
            <div style={{ color: "var(--text-muted)" }}>Keine Infos verfügbar</div>
          )}

          {contact && (
            <div style={{ marginBottom: projects.length > 0 ? 10 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, marginBottom: 6 }}>
                <User className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
                {`${contact.first_name || ""} ${contact.last_name || ""}`.trim() || `Kontakt #${contact.id}`}
                <span style={{ fontWeight: 400, fontSize: 12, color: "var(--text-muted)" }}>#{contact.id}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 8px", fontSize: 12 }}>
                {contact.email && (<>
                  <MailIcon className="w-3 h-3" style={{ color: "var(--text-muted)", marginTop: 2 }} />
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </>)}
                {contact.phone_mobile && (<>
                  <Phone className="w-3 h-3" style={{ color: "var(--text-muted)", marginTop: 2 }} />
                  <a href={`tel:${contact.phone_mobile}`}>{contact.phone_mobile} <span style={{ color: "var(--text-muted)" }}>mobil</span></a>
                </>)}
                {contact.phone_home && !contact.phone_mobile && (<>
                  <Phone className="w-3 h-3" style={{ color: "var(--text-muted)", marginTop: 2 }} />
                  <a href={`tel:${contact.phone_home}`}>{contact.phone_home}</a>
                </>)}
                {(contact.street || contact.zipcode || contact.city) && (<>
                  <MapPin className="w-3 h-3" style={{ color: "var(--text-muted)", marginTop: 2 }} />
                  <span>
                    {contact.street && <div>{contact.street}</div>}
                    {(contact.zipcode || contact.city) && <div>{[contact.zipcode, contact.city].filter(Boolean).join(" ")}</div>}
                  </span>
                </>)}
              </div>
            </div>
          )}

          {projects.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 5 }}>
                {projects.length} Projekt{projects.length === 1 ? "" : "e"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {projects.slice(0, 6).map((p) => (
                  <div
                    key={p.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "4px 6px", borderRadius: 5,
                      background: String(p.id) === String(projectId || "") ? "rgba(16,185,129,0.08)" : "transparent",
                      border: String(p.id) === String(projectId || "") ? "1px solid #10b981" : "1px solid transparent",
                      fontSize: 12,
                    }}
                  >
                    <Folder className="w-3 h-3" style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                    {p.code && (
                      <span style={{
                        background: "var(--accent-light)", color: "var(--accent-color)",
                        padding: "0 5px", borderRadius: 3, fontSize: 12, fontWeight: 600,
                      }}>{p.code}</span>
                    )}
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.name}
                    </span>
                    <span style={{
                      fontSize: 12, color: "var(--text-muted)",
                      padding: "0 5px", borderRadius: 8, background: "var(--secondary-bg)",
                    }}>
                      {getPipelineStatus(p)}
                    </span>
                  </div>
                ))}
                {projects.length > 6 && (
                  <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "2px 6px" }}>
                    + {projects.length - 6} weitere
                  </div>
                )}
              </div>
            </div>
          )}

          {contact?.id && (
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border-color)", fontSize: 12 }}>
              <a
                href={`/admin/open-hero/contact/${contact.id}?tenant=${tenantSlug}`}
                target="_blank" rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 3, color: "var(--accent-color)" }}
              >
                <ExternalLink className="w-3 h-3" /> In HERO öffnen
              </a>
            </div>
          )}
        </div>
      )}
    </span>
  )
}
